import { useEffect, useState, useContext, useRef, useMemo } from "react";
import { MaterialContext } from "../context/MaterialContext";
import { UserContext } from "../context/UserContext";
import { useParams, useNavigate } from "react-router-dom";
import singleStyle from "../styles/single.module.scss";
import FilteredLibrary from "../components/FilteredLibrary";
import {
  downloadIcon,
  editIcon,
  starFillIcon,
  starOutlineIcon,
} from "../components/Icons";
import MaterialViewCount from "../utilities/MaterialViewCount";
import ImageDownload from "../utilities/ImageDownload";
import materialSupportNotes from "../data/materialSupportNotes.json";
import {
  formatBrandMaterialName,
  getBrandDataForSlug,
} from "../utilities/brandData";

function SingleMaterial() {
  const { allMaterials, ROOT_SERVER, materialsLoaded, nikeData } =
    useContext(MaterialContext);
  const { userInformation, media, collection, changeCollection } =
    useContext(UserContext);
  const [materialData, setMaterialData] = useState(null);
  const [materialDataLoaded, setMaterialDataLoaded] = useState(false);
  const [imageNumber, setImageNumber] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDownload, setImageDownload] = useState(false);
  const [error, setError] = useState(false);
  const [infoPanel, setInfoPanel] = useState("sustainability");
  const heroImageRef = useRef(null);

  const { vendor, slug } = useParams();
  const navigate = useNavigate();

  const brandData = useMemo(() => {
    if (!materialData?.slug) {
      return [];
    }

    return getBrandDataForSlug(nikeData, materialData.slug);
  }, [materialData?.slug, nikeData]);

  const hasBrandData = brandData.length > 0;

  useEffect(() => {
    if (!materialsLoaded) {
      return;
    }

    const getMaterialData = allMaterials.filter(
      (mat) => mat.vendor === vendor && mat.slug === slug,
    );

    if (getMaterialData.length === 1) {
      setError(false);
      setMaterialData(getMaterialData[0]);
      setMaterialDataLoaded(true);
    } else {
      setError(true);
      setMaterialData(null);
      setMaterialDataLoaded(false);
    }
  }, [allMaterials, materialsLoaded, vendor, slug]);

  useEffect(() => {
    if (!materialData?.id) {
      return;
    }

    MaterialViewCount(materialData.id);
  }, [materialData?.id]);

  useEffect(() => {
    if (!materialDataLoaded || !materialData?.id) {
      return;
    }

    setImageLoaded(false);
    setImageNumber(0);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [materialDataLoaded, materialData?.id, slug, vendor]);

  useEffect(() => {
    setInfoPanel(hasBrandData ? "brand" : "sustainability");
  }, [hasBrandData, materialData?.id]);

  useEffect(() => {
    const imageElement = heroImageRef.current;

    // Cached images can be complete before onLoad runs, so sync state directly.
    if (imageElement?.complete && imageElement.naturalWidth > 0) {
      setImageLoaded(true);
    }
  }, [materialData?.id, imageNumber, media]);

  const handleImageLoaded = () => {
    setImageLoaded(true);
  };

  const handleChangeImage = (i) => {
    if (i !== imageNumber) {
      setImageLoaded(false);
      setImageNumber(i);
    }
  };

  const handleEdit = (targetVendor, targetSlug) => {
    navigate(`../material/edit/${targetVendor}/${targetSlug}`);
  };

  const handleBackToLibrary = () => {
    navigate("/library");
  };

  const getViewingImage = (material, i) => {
    if (!material) {
      return "";
    }

    const imageSet = Array.isArray(material.images) ? material.images : [];
    const safeImage = imageSet[i] || imageSet[0] || "";

    return `${ROOT_SERVER}${material.vendor}/${material.slug}/${
      media === "mobile" ? "swatch" : "full"
    }/${safeImage}`;
  };

  const handleDownloadImage = (targetVendor, targetSlug) => {
    ImageDownload(
      getViewingImage(materialData, imageNumber),
      targetVendor,
      targetSlug,
    );
    setImageDownload(true);
    setTimeout(() => {
      setImageDownload(false);
    }, 3000);
  };

  const { userId, isLoggedIn, userType } = userInformation;
  const canEdit = Boolean(userId && isLoggedIn && (userType === "vendor" || userType === "vendoradmin"));
  const isLoadedView = materialDataLoaded && materialData && !error;

  const getSupportNotes = (materialType, materialVendor) => {
    const normalizedType = String(materialType || "").toLowerCase();
    const normalizedVendor = String(materialVendor || "").toLowerCase();

    const noteSource =
      normalizedType === "split" && normalizedVendor === "tectuff"
        ? materialSupportNotes.split_tectuff
        : normalizedType === "split"
          ? materialSupportNotes.split
          : materialSupportNotes.default;

    const fallback = materialSupportNotes.default || {
      title: "Sustainability",
      info: ["--"],
    };

    return {
      title:
        noteSource && typeof noteSource.title === "string"
          ? noteSource.title
          : fallback.title,
      items:
        noteSource && Array.isArray(noteSource.info)
          ? noteSource.info
          : fallback.info,
    };
  };

  if (isLoadedView) {
    const {
      id,
      title,
      slug,
      vendor,
      subtype,
      character,
      feature,
      substrate,
      status,
      type,
      price,
      images,
      view_count,
      description,
    } = materialData;

    const supportNotes = getSupportNotes(type, vendor);
    const materialImages = Array.isArray(images) ? images : [];
    const featureList = Array.isArray(feature) ? feature.filter(Boolean) : [];
    const characterList = Array.isArray(character)
      ? character.filter(Boolean)
      : [];
    const inCollection = Array.isArray(collection) && collection.includes(id);
    const activeInfoPanel =
      hasBrandData && infoPanel === "brand" ? "brand" : "sustainability";

    return (
      <div className="content">
        <div className={singleStyle.singlePageShell}>
          <div className={singleStyle.singlePageWrap}>
            <section className={singleStyle.singleDetailLayout}>
              <div className={singleStyle.singleImagePane}>
                <div className={singleStyle.singleImageFrame}>
                  {!imageLoaded ? (
                    <div
                      className={singleStyle.singleImageLoading}
                      aria-hidden="true"
                    >
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  ) : null}
                  <img
                    ref={heroImageRef}
                    className={`${singleStyle.singleHeroImage} ${
                      imageLoaded
                        ? singleStyle.singleHeroImageLoaded
                        : singleStyle.singleHeroImagePending
                    }`}
                    src={getViewingImage(materialData, imageNumber)}
                    alt={`${vendor} - ${title}`}
                    onLoad={handleImageLoaded}
                    onError={handleImageLoaded}
                  />

                  {materialImages.length > 1 ? (
                    <div className={singleStyle.singleThumbRail}>
                      {materialImages.map((image, index) => (
                        <button
                          key={`${image}-${index}`}
                          type="button"
                          className={`${singleStyle.singleThumbButton} ${
                            index === imageNumber
                              ? singleStyle.singleThumbButtonActive
                              : ""
                          }`}
                          onClick={() => handleChangeImage(index)}
                          aria-label={`Show image ${index + 1} for ${title}`}
                        >
                          <img
                            src={`${ROOT_SERVER}${vendor}/${slug}/thumbnail/${image}`}
                            alt={`${title} thumbnail ${index + 1}`}
                          />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <aside className={singleStyle.singleInfoRail}>
                <div className={singleStyle.singleInfoMain}>
                <div className={singleStyle.singleInfoIntro}>
                  <p className={singleStyle.singleVendorLabel}>{vendor}</p>
                  <h1>{title}</h1>
                  <p className={singleStyle.singleDescription}>
                    {description || "Material details and similar options."}
                  </p>
                </div>

                <div className={singleStyle.singleMetaGrid}>
                  <div className={singleStyle.singleMetaCard}>
                    <span>Substrate</span>
                    <strong>{substrate || "--"}</strong>
                  </div>
                  <div className={singleStyle.singleMetaCard}>
                    <span>Type</span>
                    <strong>{type || "--"}</strong>
                  </div>
                  <div className={singleStyle.singleMetaCard}>
                    <span>Group</span>
                    <strong>{subtype || "--"}</strong>
                  </div>
                </div>

                <div className={singleStyle.singlePriceBlock}>
                  <div className={singleStyle.singlePriceStatusRow}>
                    <div className={singleStyle.singlePriceSide}>
                      <div className={singleStyle.singlePriceRow}>
                        <span className={singleStyle.singlePriceValue}>
                          ${Number(price || 0).toFixed(2)}
                        </span>
                        <span className={singleStyle.singlePriceUom}>
                          / sq.ft.
                        </span>
                      </div>
                    </div>
                    <div className={singleStyle.singleStatusSide}>
                      <span className={singleStyle.singleStatusLabel}>
                        Status
                      </span>
                      <strong className={singleStyle.singleStatusValue}>
                        {status || "--"}
                      </strong>
                    </div>
                  </div>
                  <p className={singleStyle.singlePriceNote}>
                    Price shown for reference only.
                  </p>
                </div>

                {characterList.length > 0 || featureList.length > 0 ? (
                  <div className={singleStyle.singleTraitsPanel}>
                    {characterList.length > 0 ? (
                      <div className={singleStyle.singleTraitGroup}>
                        <h3>Character</h3>
                        <ul className={singleStyle.singleTraitList}>
                          {characterList.map((item) => (
                            <li key={item} className={singleStyle.singleTrait}>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {featureList.length > 0 ? (
                      <div className={singleStyle.singleTraitGroup}>
                        <h3>Finish</h3>
                        <ul className={singleStyle.singleTraitList}>
                          {featureList.map((item) => (
                            <li key={item} className={singleStyle.singleTrait}>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className={singleStyle.singleSupportPanel}>
                  {hasBrandData ? (
                    <div
                      className={singleStyle.singleSupportTabs}
                      role="tablist"
                      aria-label="Material notes"
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeInfoPanel === "brand"}
                        className={`${singleStyle.singleSupportTab} ${
                          activeInfoPanel === "brand"
                            ? singleStyle.singleSupportTabActive
                            : ""
                        }`}
                        onClick={() => setInfoPanel("brand")}
                      >
                        Brand Info
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeInfoPanel === "sustainability"}
                        className={`${singleStyle.singleSupportTab} ${
                          activeInfoPanel === "sustainability"
                            ? singleStyle.singleSupportTabActive
                            : ""
                        }`}
                        onClick={() => setInfoPanel("sustainability")}
                      >
                        {supportNotes.title}
                      </button>
                    </div>
                  ) : (
                    <h3 className={singleStyle.singleSupportTitle}>
                      {supportNotes.title}
                    </h3>
                  )}

                  <div className={singleStyle.singleSupportBody}>
                    {activeInfoPanel === "brand" ? (
                      <ul className={singleStyle.singleBrandList}>
                        {brandData.map((data, index) => (
                          <li
                            key={`${data.pcx_material_number || "pcx"}-${index}`}
                          >
                            <span className={singleStyle.singleBrandName}>
                              {formatBrandMaterialName(data, title)}
                            </span>
                            <span className={singleStyle.singleBrandMeta}>
                              {[
                                data.thickness != null && data.thickness !== ""
                                  ? `${data.thickness}mm`
                                  : null,
                                data.pcx_material_number != null
                                  ? `PCX# ${data.pcx_material_number}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul className={singleStyle.singleNoteList}>
                        {supportNotes.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                </div>

                <div className={singleStyle.singleRailFooter}>
                  <div className={singleStyle.singleActionRow}>
                    <button
                      type="button"
                      className={`${singleStyle.singleRailPrimaryButton} ${singleStyle.singleRailSecondaryButton}`}
                      onClick={() => changeCollection(id)}
                    >
                      {inCollection ? starFillIcon : starOutlineIcon}
                      <span>
                        {inCollection ? "In Collection" : "Add to Collection"}
                      </span>
                    </button>

                    <button
                      type="button"
                      className={singleStyle.singleRailPrimaryButton}
                      onClick={() => handleDownloadImage(vendor, slug)}
                    >
                      {downloadIcon}
                      <span>
                        {imageDownload ? "Downloading" : "Download Image"}
                      </span>
                    </button>

                    <button
                      type="button"
                      className={`${singleStyle.singleRailPrimaryButton} ${singleStyle.singleRailBackButton}`}
                      onClick={handleBackToLibrary}
                    >
                      <svg
                        className={singleStyle.singleBackChevron}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M12.5 4.5L7 10l5.5 5.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className={singleStyle.singleBackLabel}>
                        Back to Library
                      </span>
                    </button>
                  </div>

                  {canEdit ? (
                    <div className={singleStyle.singleVendorTools}>
                      <p className={singleStyle.singleViewCount}>
                        View count: {view_count}
                      </p>
                      <button
                        type="button"
                        className={`${singleStyle.singleRailGhostButton} ${singleStyle.singleVendorEditButton}`}
                        onClick={() => handleEdit(vendor, slug)}
                      >
                        {editIcon}
                        <span>Edit</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              </aside>
            </section>
          </div>

          <div className={singleStyle.singleSimilar}>
            <FilteredLibrary
              id={id}
              terms={character}
              vendor={vendor}
              title={title}
              allMaterials={allMaterials}
              control={subtype.toLowerCase()}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!materialsLoaded || (!materialDataLoaded && !error)) {
    return (
      <div className="content">
        <div className={singleStyle.singlePageLoading}>
          <h3>{vendor.replaceAll("-", " ").toUpperCase()}</h3>
          <h2>{slug.replaceAll("-", " ").toUpperCase()}</h2>
          <div className={singleStyle.singlePageLoadingDots} aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="empty-collection">
        <div className="empty-collection-message">
          <div className="text-box">
            <h2 className="login-view-heading">
              Can't find {slug} from {vendor}
            </h2>
            <button className="button" onClick={handleBackToLibrary}>
              return to library
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SingleMaterial;
